// Edit an existing outing (owner only — RLS enforces it server-side, the UI
// checks the owner outing_person row to fail fast with a friendly message).
// Field edits go through one updateOuting patch; participant and route-link
// changes are diffed against the loaded rows and applied as create/update/
// delete mutations keyed on their natural keys.
import { useState, type ChangeEvent, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { gqlRequest } from "../lib/gql";
import {
  CREATE_OUTING_PERSON,
  CREATE_OUTING_PHOTO,
  CREATE_OUTING_ROUTE,
  DELETE_OUTING_PERSON,
  DELETE_OUTING_PHOTO,
  DELETE_OUTING_ROUTE,
  IMPORT_TRACK,
  OUTING_DETAIL,
  UPDATE_OUTING,
  UPDATE_OUTING_PHOTO,
  UPDATE_OUTING_ROUTE,
  type ImportTrackResult,
  type OutingDetailResult,
  type PersonSummary,
  type UpdateOutingResult,
} from "../lib/queries";
import { X } from "lucide-react";
import { useAuth } from "../lib/auth";
import { thumbUrl, uploadPhoto } from "../lib/photos";
import { parseGpx, type ParsedGpx } from "../lib/gpx";
import {
  ACTIVITIES,
  ACTIVITY_ICONS,
  CONDITIONS,
  formatDistance,
  formatElevation,
  OUTING_STATUSES,
  OUTING_VISIBILITIES,
  type Activity,
  type ConditionRating,
  type OutingStatus,
  type OutingVisibility,
} from "../lib/format";
import { useT } from "../lib/i18n";
import { LoginGate } from "../components/LoginGate";
import { PersonPicker } from "../components/PersonPicker";
import { PhotoPicker, type NewPhoto } from "../components/PhotoPicker";
import { RouteLinkPicker, type RouteLink } from "../components/RouteLinkPicker";
import {
  Chip,
  EmptyState,
  ErrorBox,
  Field,
  Select,
  Spinner,
  Stars,
  TextArea,
  TextInput,
} from "../components/ui";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";

export function EditOutingPage({ id }: { id: string }) {
  return (
    <LoginGate action="gateAction.editOuting" next={`/outing/${id}/edit`}>
      <EditOutingLoader id={id} />
    </LoginGate>
  );
}

type Translate = ReturnType<typeof useT>["t"];

function friendlyError(err: unknown, t: Translate): string {
  const msg = err instanceof Error ? err.message : String(err);
  // RLS filtering the row out surfaces as "no row matched" from pdbq.
  if (/no row matched|permission|denied|policy|violates row-level/i.test(msg)) return t("edit.noPermission");
  if (/violates|constraint|invalid input/i.test(msg)) return t("err.rejected", { msg });
  return msg;
}

function EditOutingLoader({ id }: { id: string }) {
  const { personId } = useAuth();
  const { t } = useT();

  const { data, isLoading, error } = useQuery({
    queryKey: ["outing", id],
    queryFn: () => gqlRequest<OutingDetailResult>(OUTING_DETAIL, { id }),
  });

  if (isLoading) return <Spinner />;
  if (error) return <ErrorBox error={error} />;
  const outing = data?.outing;
  if (!outing) return <EmptyState>{t("outing.notFound")}</EmptyState>;

  const owner = outing.outingPeopleByOutingId.nodes.find((p) => p.role === "OWNER");
  if (owner?.personId !== personId) {
    return <EmptyState>{t("edit.notOwner")}</EmptyState>;
  }

  return <EditOutingForm outing={outing} ownerId={owner.personId} />;
}

function EditOutingForm({
  outing,
  ownerId,
}: {
  outing: NonNullable<OutingDetailResult["outing"]>;
  ownerId: string;
}) {
  const { t, labelizeT } = useT();
  const [, navigate] = useLocation();
  const queryClient = useQueryClient();

  const initialParticipants: PersonSummary[] = outing.outingPeopleByOutingId.nodes
    .filter((p) => p.role === "PARTICIPANT")
    .map((p) => ({ id: p.personId, displayName: p.displayName, activities: null, avatarKey: p.person?.avatarKey ?? null }));

  // Already ordered by sort server-side (OUTING_DETAIL orders the connection).
  const initialRouteLinks: RouteLink[] = outing.outingRoutesByOutingId.nodes.map((n) => ({
    route: { id: n.route.id, name: n.route.name, activities: n.route.activities, durationMin: n.route.durationMin },
    style: n.style ?? "",
    result: n.result ?? "",
  }));

  const [title, setTitle] = useState(outing.title);
  const [date, setDate] = useState(outing.date);
  const [activity, setActivity] = useState<Activity>(outing.activity);
  const [status, setStatus] = useState<OutingStatus>(outing.status);
  const [conditions, setConditions] = useState<ConditionRating | "">(outing.conditions ?? "");
  const [rating, setRating] = useState<number | null>(outing.rating);
  const [description, setDescription] = useState(outing.description ?? "");
  const [visibility, setVisibility] = useState<OutingVisibility>(outing.visibility);
  const [participants, setParticipants] = useState<PersonSummary[]>(initialParticipants);
  const [routeLinks, setRouteLinks] = useState<RouteLink[]>(initialRouteLinks);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  // Track edits: "keep" leaves trackId out of the patch entirely. Removing or
  // replacing orphans the old track row — deleteTrack is permission-denied at
  // runtime, and orphaned tracks are acceptable.
  const [trackEdit, setTrackEdit] = useState<
    { kind: "keep" } | { kind: "remove" } | { kind: "replace"; parsed: ParsedGpx; raw: string; fileName: string }
  >({ kind: "keep" });
  const [gpxError, setGpxError] = useState<string | null>(null);
  // Existing photo rows: caption edits and removals are diffed on save.
  // Deleting a row orphans the file on photod's disk, which is acceptable.
  const [existingPhotos, setExistingPhotos] = useState(
    outing.outingPhotos.nodes.map((p) => ({ ...p, caption: p.caption ?? "", removed: false })),
  );
  const [newPhotos, setNewPhotos] = useState<NewPhoto[]>([]);

  async function onGpxSelect(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const raw = await file.text();
      setTrackEdit({ kind: "replace", parsed: parseGpx(raw), raw, fileName: file.name });
      setGpxError(null);
    } catch (err) {
      setGpxError(err instanceof Error ? err.message : String(err));
    }
  }

  const planned = status === "PLANNED";

  const save = useMutation({
    mutationFn: async () => {
      // Saving as PLANNED clears what a planned outing can't have yet
      // (conditions, rating, time) and ignores any pending track edit; the
      // existing track and photos survive untouched.
      const patch: Record<string, unknown> = {
        title: title.trim(),
        date,
        activity,
        status,
        conditions: planned ? null : conditions || null,
        rating: planned ? null : rating,
        description: description.trim() || null,
        visibility,
      };
      if (planned) {
        patch.elapsedSeconds = null;
      } else if (trackEdit.kind === "remove") {
        patch.trackId = null;
      } else if (trackEdit.kind === "replace") {
        const imported = await gqlRequest<ImportTrackResult>(IMPORT_TRACK, {
          geojson: trackEdit.parsed.geojson,
          pointTimes: trackEdit.parsed.pointTimes,
          sourceGpx: trackEdit.raw,
        });
        patch.trackId = imported.importTrack.result.id;
      }
      const result = await gqlRequest<UpdateOutingResult>(UPDATE_OUTING, { id: outing.id, patch });
      if (!result.updateOuting?.outing) throw new Error(t("edit.noPermission"));

      const before = new Set(initialParticipants.map((p) => p.id));
      const after = new Set(participants.map((p) => p.id));
      for (const p of participants) {
        if (!before.has(p.id)) await gqlRequest(CREATE_OUTING_PERSON, { outingId: outing.id, personId: p.id });
      }
      for (const p of initialParticipants) {
        if (!after.has(p.id)) await gqlRequest(DELETE_OUTING_PERSON, { outingId: outing.id, personId: p.id });
      }

      // Route links diffed by route id against the loaded rows: delete removed,
      // create added, patch changed style/result/sort. All three mutations use
      // the (outing_id, route_id) natural key; RLS grants the owner update, so
      // no delete+recreate dance is needed.
      const linkBefore = new Map(outing.outingRoutesByOutingId.nodes.map((n) => [n.route.id, n]));
      const linkAfter = new Set(routeLinks.map((l) => l.route.id));
      for (const routeId of linkBefore.keys()) {
        if (!linkAfter.has(routeId)) await gqlRequest(DELETE_OUTING_ROUTE, { outingId: outing.id, routeId });
      }
      for (const [sort, link] of routeLinks.entries()) {
        const style = link.style || null;
        const result = link.result.trim() || null;
        const prev = linkBefore.get(link.route.id);
        if (!prev) {
          await gqlRequest(CREATE_OUTING_ROUTE, { outingId: outing.id, routeId: link.route.id, sort, style, result });
        } else if (prev.sort !== sort || prev.style !== style || prev.result !== result) {
          await gqlRequest(UPDATE_OUTING_ROUTE, {
            outingId: outing.id,
            routeId: link.route.id,
            patch: { sort, style, result },
          });
        }
      }

      // Photo edits only apply for completed outings — the section is hidden
      // when PLANNED, so pending edits made before the switch are discarded.
      if (!planned) {
        const initialByPhotoId = new Map(outing.outingPhotos.nodes.map((p) => [p.id, p]));
        for (const p of existingPhotos) {
          if (p.removed) {
            await gqlRequest(DELETE_OUTING_PHOTO, { id: p.id });
          } else if (p.caption !== (initialByPhotoId.get(p.id)?.caption ?? "")) {
            await gqlRequest(UPDATE_OUTING_PHOTO, { id: p.id, patch: { caption: p.caption.trim() || null } });
          }
        }
        // New photos sort after the surviving existing ones, in selection order.
        const maxSort = existingPhotos.filter((p) => !p.removed).reduce((m, p) => Math.max(m, p.sort), -1);
        for (const [index, photo] of newPhotos.entries()) {
          const storageKey = await uploadPhoto(photo.file);
          await gqlRequest(CREATE_OUTING_PHOTO, {
            outingId: outing.id,
            storageKey,
            caption: photo.caption.trim() || null,
            sort: maxSort + 1 + index,
          });
        }
      }
    },
    // onSettled, not onSuccess: the patch may have landed even when a later
    // participant mutation failed, so the cache must refetch either way.
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["outing", outing.id] });
      queryClient.invalidateQueries({ queryKey: ["feed"] });
    },
    onSuccess: () => {
      navigate(`/outing/${outing.id}`);
    },
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    const errors: Record<string, string> = {};
    if (!title.trim()) errors.title = t("newOuting.errTitle");
    if (!date) errors.date = t("newOuting.errDate");
    else if (!planned && date > new Date().toISOString().slice(0, 10)) errors.date = t("newOuting.errFuture");
    setFieldErrors(errors);
    if (Object.keys(errors).length === 0) save.mutate();
  }

  return (
    <div className="mx-auto max-w-xl p-4">
      <h1 className="mb-5 text-2xl font-semibold tracking-tight text-foreground">{t("edit.title")}</h1>
      <form onSubmit={onSubmit} className="space-y-4">
        <Field label={t("form.title")} error={fieldErrors.title}>
          <TextInput value={title} onChange={(e) => setTitle(e.target.value)} />
        </Field>

        <Field label={t("form.status")}>
          <div className="flex flex-wrap gap-2">
            {OUTING_STATUSES.map((s) => (
              <Chip key={s} active={status === s} onClick={() => setStatus(s)}>
                {labelizeT(s)}
              </Chip>
            ))}
          </div>
          <p className="mt-1 text-xs text-muted-foreground">{t("newOuting.plannedHint")}</p>
        </Field>

        <Field label={t("form.date")} error={fieldErrors.date}>
          <TextInput type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </Field>

        {!planned && (
          <Field label={t("edit.gpx")} error={gpxError ?? undefined}>
            {trackEdit.kind === "replace" ? (
              <div className="flex items-center justify-between rounded-xl border bg-accent px-3 py-2">
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium text-foreground">{trackEdit.fileName}</span>
                  <span className="block text-xs text-muted-foreground">
                    {t("gpx.stats", { count: trackEdit.parsed.pointCount, distance: formatDistance(trackEdit.parsed.distanceM) })}
                    {outing.track ? ` · ${t("edit.replacesOnSave")}` : ""}
                  </span>
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => setTrackEdit({ kind: "keep" })}
                  className="ml-2 shrink-0 text-muted-foreground hover:text-destructive"
                  aria-label={t("edit.cancelTrackAria")}
                >
                  <X aria-hidden className="size-4" />
                </Button>
              </div>
            ) : trackEdit.kind === "remove" ? (
              <div className="flex items-center justify-between rounded-xl border border-destructive/30 bg-destructive/10 px-3 py-2">
                <span className="text-sm text-destructive">{t("edit.trackRemoved")}</span>
                <button
                  type="button"
                  onClick={() => setTrackEdit({ kind: "keep" })}
                  className="ml-2 shrink-0 text-sm font-semibold text-muted-foreground hover:underline"
                >
                  {t("common.undo")}
                </button>
              </div>
            ) : outing.track ? (
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border bg-accent px-3 py-2">
                <span className="text-sm text-foreground">
                  {t("edit.trackAttached", {
                    distance: formatDistance(outing.track.distanceM),
                    ascent: formatElevation(outing.track.ascentM),
                  })}
                </span>
                <span className="flex items-center gap-3">
                  <label className="cursor-pointer text-sm font-semibold text-primary hover:underline">
                    {t("edit.replace")}
                    <input type="file" accept=".gpx" className="hidden" onChange={onGpxSelect} />
                  </label>
                  <button
                    type="button"
                    onClick={() => setTrackEdit({ kind: "remove" })}
                    className="text-sm font-semibold text-destructive hover:underline"
                  >
                    {t("common.remove")}
                  </button>
                </span>
              </div>
            ) : (
              <input
                type="file"
                accept=".gpx"
                onChange={onGpxSelect}
                className="block w-full text-sm text-muted-foreground file:mr-3 file:rounded-lg file:border-0 file:bg-primary/10 file:px-3 file:py-1.5 file:text-sm file:font-semibold file:text-primary hover:file:bg-primary/20"
              />
            )}
          </Field>
        )}

        <Field label={t("form.activity")}>
          <div className="flex flex-wrap gap-2">
            {ACTIVITIES.map((a) => {
              const Icon = ACTIVITY_ICONS[a];
              return (
                <Chip key={a} active={activity === a} onClick={() => setActivity(a)}>
                  <span className="inline-flex items-center gap-1.5">
                    <Icon aria-hidden className="size-3.5" />
                    {labelizeT(a)}
                  </span>
                </Chip>
              );
            })}
          </div>
        </Field>

        {!planned && (
          <div className="grid grid-cols-2 items-end gap-3">
            <Field label={t("form.conditions")}>
              <Select value={conditions} onChange={(e) => setConditions(e.target.value as ConditionRating | "")}>
                <option value="">{t("common.notSpecified")}</option>
                {CONDITIONS.map((c) => (
                  <option key={c} value={c}>
                    {labelizeT(c)}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t("form.rating")}>
              <Stars rating={rating} onChange={setRating} />
            </Field>
          </div>
        )}

        <Field label={t("form.notes")}>
          <TextArea
            rows={4}
            value={description}
            placeholder={t("newOuting.notesPlaceholder")}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Field>

        {!planned && (
          <Field label={t("edit.photos")}>
            <div className="space-y-2">
              {existingPhotos.length > 0 && (
                <ul className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {existingPhotos.map((p, i) => (
                    <li
                      key={p.id}
                      className={`overflow-hidden rounded-xl border bg-card shadow-card ${
                        p.removed ? "border-destructive/30 opacity-60" : "border-border"
                      }`}
                    >
                      <div className="relative">
                        <img src={thumbUrl(p.storageKey, 640, 640)} alt={p.caption} className="aspect-square w-full object-cover" />
                        {p.removed ? (
                          <button
                            type="button"
                            onClick={() =>
                              setExistingPhotos((prev) => prev.map((x, j) => (j === i ? { ...x, removed: false } : x)))
                            }
                            className="absolute inset-x-0 bottom-0 bg-destructive/80 py-1 text-center text-xs font-semibold text-white"
                          >
                            {t("photo.removedUndo")}
                          </button>
                        ) : (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() =>
                              setExistingPhotos((prev) => prev.map((x, j) => (j === i ? { ...x, removed: true } : x)))
                            }
                            aria-label={t("photo.removeAria")}
                            className="absolute right-1 top-1 size-6 rounded-full bg-card/90 text-muted-foreground shadow-card hover:text-destructive"
                          >
                            <X aria-hidden className="size-3.5" />
                          </Button>
                        )}
                      </div>
                      {!p.removed && (
                        <div className="p-1.5">
                          <TextInput
                            value={p.caption}
                            placeholder={t("photo.caption")}
                            onChange={(e) =>
                              setExistingPhotos((prev) =>
                                prev.map((x, j) => (j === i ? { ...x, caption: e.target.value } : x)),
                              )
                            }
                            className="!px-2 !py-1 !text-xs"
                          />
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              <PhotoPicker photos={newPhotos} onChange={setNewPhotos} />
            </div>
          </Field>
        )}

        <Field label={t("edit.routes")}>
          <RouteLinkPicker links={routeLinks} onChange={setRouteLinks} />
        </Field>

        <Field label={t("edit.participants")}>
          <PersonPicker selected={participants} onChange={setParticipants} excludeIds={[ownerId]} />
        </Field>

        <Field label={t("form.visibility")}>
          <Select value={visibility} onChange={(e) => setVisibility(e.target.value as OutingVisibility)}>
            {OUTING_VISIBILITIES.map((v) => (
              <option key={v} value={v}>
                {labelizeT(v)}
              </option>
            ))}
          </Select>
          <p className="mt-1 text-xs text-muted-foreground">{t("newOuting.followersHint")}</p>
        </Field>

        {save.error != null && (
          <Alert variant="destructive">
            <AlertDescription>{friendlyError(save.error, t)}</AlertDescription>
          </Alert>
        )}

        <div className="flex gap-3">
          <Button type="submit" disabled={save.isPending} className="flex-1 py-2.5">
            {save.isPending ? t("common.saving") : t("edit.submit")}
          </Button>
          <Button type="button" variant="outline" onClick={() => navigate(`/outing/${outing.id}`)}>
            {t("common.cancel")}
          </Button>
        </div>
      </form>
    </div>
  );
}
